import * as moment from 'moment';
import * as _ from 'lodash';
import {IParsedKN15Telegram} from './kn15-telegram/kn15-parse.service';
import {IStation, IDischargeModel, IDailyTriplet} from '../../api/api.types';
import {
    IDataValueApi,
    IDailyDataRequest
} from '../../api/data-value-api.service';
import {IOperationalInputService, IPartialDailyData} from './input.service';
import dischargeCardComponent from "./kn15-telegram/discharge-card.component";
import {IParsedTelegramData} from './telegram.controller';
import {ITelegramComparsionDailyDataRow} from './telegram-data-comparison.service';

export interface IProcessedDataCell {
  waterLevel: number;
  discharge: number;
  highlighted: boolean;
}

export interface IProcessedDataRow {
  date: moment.Moment;
  morningCell?: IProcessedDataCell;
  eveningCell?: IProcessedDataCell;
  averageCell?: IProcessedDataCell;
}

export class DailyDataService {
  static $inject = ['dataValueApi', 'operationalInputService', '$q', '$log', 'dischargeModelApi'];
  /**
   * Parsed telegram with the data to process and save.
   */
  parsedTelegram: IParsedKN15Telegram;
  /**
   * Station from the telegram.
   */
  station: IStation;
  /**
   * Discharge model to use for processing.
   */
  dischargeModel: IDischargeModel;
  /**
   * Whether the discharge model was created in this session or not.
   */
  createdDischargeModel: boolean;
  /**
   * Data rows display on the table.
   */
  dataRows: IProcessedDataRow[];

  /**
   * Daily data retrieved from the backend.
   */
  private dailyData: IDailyTriplet[];
  /**
   * Starting overriddenDate for the discharge calculations.
   */
  private startDate: moment.Moment;
  /**
   * End overriddenDate for the discharge calculations.
   */
  private endDate: moment.Moment;

  constructor(private dataValueApi: IDataValueApi,
              private operationalInputService: IOperationalInputService,
              private $q: ng.IQService, private $log: ng.ILogService) {
  }

  private calculateAvg(dataRow: IProcessedDataRow) {

    let discharges: number[] = [];

    if (dataRow.morningCell && dataRow.morningCell.discharge && !isNaN(dataRow.morningCell.discharge)) {
      discharges.push(dataRow.morningCell.discharge);
    }

    if (dataRow.eveningCell && dataRow.eveningCell.discharge && !isNaN(dataRow.eveningCell.discharge)) {
      discharges.push(dataRow.eveningCell.discharge);
    }

    let sum = 0;

    _.forEach(discharges, function (discharge: number) {
      sum += discharge;
    });

    return sum / discharges.length;
  }

  public getPreviousDailyData(parsedTelegram: IParsedTelegramData, dischargeModel: IDischargeModel, createdDischargeModel: boolean) {
    if (createdDischargeModel) {
      this.startDate = moment.unix(dischargeModel.validFrom)
          .startOf('day');
    } else {
      this.startDate = moment(parsedTelegram.overriddenDate)
          .clone().subtract({day: 1}).startOf('day');
    }
    this.endDate = moment(parsedTelegram.overriddenDate)
        .clone().subtract({day: 1}).endOf('day');

    const deferred = this.$q.defer<IDailyTriplet[]|any>();

    const stationId = parsedTelegram.station.id;
    const startDate = this.startDate;
    const endDate = this.endDate;
    this.dataValueApi.getDailyData(stationId, startDate, endDate)
        .then(dailyData => {
          deferred.resolve(dailyData);
        })
        .catch((error) => {
          deferred.reject(error);
        });
    return deferred.promise;
  }

  public getDataRows(dailyData: IDailyTriplet[], parsedTelegram: IParsedTelegramData, dischargeModel: IDischargeModel, previousDischargeModel: IDischargeModel) {
    const partialDailyData: IPartialDailyData[] = dailyData 
        .map(dailyTriplet => {
          const partialDatum: IPartialDailyData = {
            date: extractDate(dailyTriplet)
          };
          if (dailyTriplet.eightData != null) {
            partialDatum.morningWaterLevel = dailyTriplet.eightData
                .waterLevel.dataValue;
            partialDatum.morningDischarge = dailyTriplet.eightData
                .discharge.dataValue;
          }
          if (dailyTriplet.twentyData != null) {
            partialDatum.eveningWaterLevel = dailyTriplet.twentyData
                .waterLevel.dataValue;
          }
          if (dailyTriplet.averageData != null) {
            partialDatum.averageWaterLevel = dailyTriplet.averageData
                .waterLevel.dataValue;
          }
          return partialDatum;
        });
    const previousTelegramDay = moment(parsedTelegram.overriddenDate)
        .clone().subtract({day: 1});
    let previousDayDatum: IPartialDailyData = null;
    if (partialDailyData && partialDailyData.length &&
        partialDailyData.slice(-1)[0].date.isSame(previousTelegramDay, 'day')) {
      previousDayDatum = partialDailyData.slice(-1)[0];
    } else {
      previousDayDatum = {
        date: previousTelegramDay
      };
      partialDailyData.push(previousDayDatum);
    }
    previousDayDatum.eveningWaterLevel = parsedTelegram
        .waterLevelData.previousWaterLevelAtTwenty;
    previousDayDatum.averageWaterLevel = this.operationalInputService
        .calculateAverageWaterLevel(previousDayDatum.eveningWaterLevel,
            previousDayDatum.morningWaterLevel);
    const currentDayDatum: IPartialDailyData = {
      date: moment(parsedTelegram.overriddenDate),
      morningWaterLevel: parsedTelegram.waterLevelData.waterLevelAtEight
    };
    partialDailyData.push(currentDayDatum);
    return this.operationalInputService
        .calculateDailyDischarge(partialDailyData, dischargeModel, previousDischargeModel)
  }

  public getProcessedData(dataRows: IProcessedDataRow[]) {
    const processedData: IDailyDataRequest[] = dataRows
        .map(dataRow => {
          const dailyDataRequest: IDailyDataRequest = {
            date: dataRow.date.clone(),
            morningData: null,
            eveningData: null,
            averageData: null
          };
          if (dataRow.morningCell) {
            dailyDataRequest.morningData = {
              waterLevel: dataRow.morningCell.waterLevel,
              discharge: dataRow.morningCell.discharge
            };
          }
          if (dataRow.eveningCell) {
            dailyDataRequest.eveningData = {
              waterLevel: dataRow.eveningCell.waterLevel,
              discharge: dataRow.eveningCell.discharge
            };
          }
          if (dataRow.averageCell) {
            dailyDataRequest.averageData = {
              waterLevel: dataRow.averageCell.waterLevel,
              discharge: dataRow.averageCell.discharge
            };
          }
          return dailyDataRequest;
        });
    return processedData;
  }

  public getDailyDataAtOnce (parsedTelegram: IParsedTelegramData, dischargeModel: IDischargeModel, createdDischargeModel: boolean, previousDischargeModel: IDischargeModel) {
    const deferred = this.$q.defer<IDailyTriplet[]|any>();
    this.getPreviousDailyData(parsedTelegram, dischargeModel, createdDischargeModel)
      .then((dailyData) => {
          deferred.resolve(
            this.getProcessedData(this.getDataRows(dailyData, parsedTelegram, dischargeModel, previousDischargeModel))
          );
      })
      .catch((error) => {
        deferred.reject(error)
      });
    return deferred.promise;
  }

  public formatDailyDataRequest(dailyData: ITelegramComparsionDailyDataRow[]) {
    let formattedDailyData: IDailyDataRequest[] = [];
    dailyData.forEach((dailyDataItem) => {
      let formattedDailyDataItem: IDailyDataRequest = {
        date: dailyDataItem.date,
        morningData: {
          waterLevel: null,
          discharge: null
        },
        eveningData: {
          waterLevel: null,
          discharge: null
        },
        averageData: {
          waterLevel: null,
          discharge: null
        }
      };

      // morning
      formattedDailyDataItem.morningData.waterLevel = 
        dailyDataItem.morningCell.overrideWaterLevelData ? 
          dailyDataItem.morningCell.newWaterLevel || null:
          dailyDataItem.morningCell.oldWaterLevel || null;

      formattedDailyDataItem.morningData.discharge = 
        dailyDataItem.morningCell.overrideDischargeData ? 
          dailyDataItem.morningCell.newDischarge || null:
          dailyDataItem.morningCell.oldDischarge || null;

      // evening
      formattedDailyDataItem.eveningData.waterLevel = 
        dailyDataItem.eveningCell.overrideWaterLevelData ? 
          dailyDataItem.eveningCell.newWaterLevel || null:
          dailyDataItem.eveningCell.oldWaterLevel || null;

      formattedDailyDataItem.eveningData.discharge = 
        dailyDataItem.eveningCell.overrideDischargeData ? 
          dailyDataItem.eveningCell.newDischarge || null:
          dailyDataItem.eveningCell.oldDischarge || null;

      // average
      formattedDailyDataItem.averageData.waterLevel = 
        dailyDataItem.averageCell.overrideWaterLevelData ? 
          dailyDataItem.averageCell.newWaterLevel || null:
          dailyDataItem.averageCell.oldWaterLevel || null;

      formattedDailyDataItem.averageData.discharge = 
        dailyDataItem.averageCell.overrideDischargeData ? 
          dailyDataItem.averageCell.newDischarge || null:
          dailyDataItem.averageCell.oldDischarge || null;

      formattedDailyData.push(formattedDailyDataItem);

    });

    return formattedDailyData;
  }

}

function extractDate(dailyTriplet: IDailyTriplet): moment.Moment {
  if (dailyTriplet.eightData) {
    return moment.unix(dailyTriplet.eightData.waterLevel.dateTimeUtc);
  } else if (dailyTriplet.twentyData) {
    return moment.unix(dailyTriplet.twentyData.waterLevel.dateTimeUtc);
  }
  return null;
}



/** WEBPACK FOOTER **
 ** ./app/operational/input/daily-data.service.ts
 **/